// Secrets page model (Plan secrets s4): grouping by profile, staleness, name rules. Wire shapes and the calls are in
// @/api (api.secrets); values never appear here or there: metadata through GraphQL, only ciphertext to /secrets.
import type { SecretRow, SecretsData } from "@/api";

export const KEY = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/;
export const PROFILE = /^[a-z0-9][a-z0-9_-]{0,62}$/;

export interface Group {
	profile: string;
	rows: (SecretRow & { stale: boolean })[];
}

const same = (a: readonly string[], b: readonly string[]) => a.length === b.length && new Set([...a, ...b]).size === a.length;

/** By profile (default first), keys sorted; stale = encrypted to another set than the current recipients (unknown for
 *  a connected instance's project). */
export function groups(d: SecretsData): Group[] {
	const current = d.recipients.map((r) => r.key);
	const by = new Map<string, Group>();
	for (const s of d.secrets) {
		if (s.status !== "active") continue;
		const g = by.get(s.profile) ?? { profile: s.profile, rows: [] };
		g.rows.push({ ...s, stale: !d.remote && !same(s.recipients, current) });
		by.set(s.profile, g);
	}
	return [...by.values()]
		.sort((a, b) => (a.profile === "default" ? -1 : b.profile === "default" ? 1 : a.profile.localeCompare(b.profile)))
		.map((g) => ({ ...g, rows: g.rows.sort((a, b) => a.key.localeCompare(b.key)) }));
}

/** age1qyz…p4kd: enough to compare with `hq recipient`, short enough for a phone. */
export const shortKey = (k: string) => (k.length > 16 ? `${k.slice(0, 8)}\u2026${k.slice(-6)}` : k);

/** Problems with a new value's name, or null. */
export function invalid(key: string, profile: string): "key" | "profile" | null {
	if (!KEY.test(key)) return "key";
	if (!PROFILE.test(profile)) return "profile";
	return null;
}
